import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { collection } from '@/infra/collection'
import {
  RequestedSupportingItem,
  type RequestedSupportingItemProps,
} from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { SupportingItemInput } from './supporting-item-input'

/**
 * Props for {@link Checklist}.
 */
export type ChecklistProps = ModelProps<Checklist> & {
  sections: ChecklistSectionProps[]
}

/**
 * A checklist of items to complete for a job.
 */
export class Checklist extends Model<Checklist> {
  /**
   * ID of the job that the checklist is for.
   */
  jobId = Id.none

  /**
   * List of sections.
   */
  readonly sections = collection({
    create: () => new ChecklistSection(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  /**
   * All items in the checklist.
   */
  @computed get allItems() {
    return this.sections.items.flatMap((s) => s.requestedSupportingItems.items)
  }

  /**
   * Whether the checklist has at least one item.
   */
  @computed get isPopulated() {
    return this.allItems.length > 0
  }

  /**
   * Determines the completion state of the checklist.
   */
  @computed get completion(): ChecklistCompletionState {
    if (!this.isPopulated) {
      return { type: 'Empty' }
    }

    const incompleteItems = this.allItems.filter((item) => !item.isCompleted)

    if (incompleteItems.length > 0) {
      return {
        type: 'Incomplete',
        incompleteItems,
        allRequiredItemsCompleted: this.allItems.every(
          (item) => !item.required || item.isCompleted,
        ),
      }
    }

    return { type: 'Completed' }
  }

  /**
   * Gets a requested supporting item by ID.
   *
   * @param id
   */
  getItem(id: Id) {
    return this.allItems.find((i) => i.id === id) ?? null
  }

  /**
   * Gets all requested supporting items by input type.
   *
   * @param type
   * @returns
   */
  getItemsByInputType<T extends new () => SupportingItemInput>(type: T) {
    return this.allItems.filter(
      (i): i is RequestedSupportingItem<InstanceType<T>> =>
        i.input instanceof type,
    )
  }

  /** @inheritDoc */
  @action.bound
  override set({ sections, ...props }: Partial<ChecklistProps>): this {
    if (sections) {
      this.sections.reset(sections)
    }

    return super.set(props)
  }
}

/**
 * Props for {@link ChecklistSection}.
 */
export type ChecklistSectionProps = ModelProps<ChecklistSection> & {
  requestedSupportingItems: RequestedSupportingItemProps[]
}

/**
 * A section within a checklist.
 */
export class ChecklistSection extends Model<ChecklistSection> {
  /**
   * ID of the section.
   */
  id = Id.none

  @observable accessor name = ''
  @observable accessor heading = ''
  @observable accessor description = ''

  /**
   * The list of requested supporting items.
   */
  readonly requestedSupportingItems = collection({
    create: () => new RequestedSupportingItem(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  /**
   * The count of items in the section.
   */
  @computed
  get itemCount() {
    return this.requestedSupportingItems.length
  }

  /**
   * The count of completed items in the section.
   */
  @computed
  get completedCount() {
    return this.requestedSupportingItems.filter((item) => item.isCompleted)
      .length
  }

  /** @inheritDoc */
  @action.bound
  override set({
    requestedSupportingItems,
    ...props
  }: Partial<ChecklistSectionProps>): this {
    if (requestedSupportingItems) {
      this.requestedSupportingItems.reset(requestedSupportingItems)
    }

    return super.set(props)
  }
}

/**
 * List of all well-known section category IDs.
 */
export const allWellKnownSectionCategoryIds = [
  'PERSONAL_INFORMATION',
  'PERSONAL_DOCUMENTATION',
  'BUSINESS_INFORMATION',
] as const

/**
 * Well-known IDs of section categories.
 */
export type WellKnownSectionCategoryId =
  (typeof allWellKnownSectionCategoryIds)[number]

/**
 * The checklist's completion state.
 */
export type ChecklistCompletionState = Empty | Incomplete | Completed

/**
 * The checklist is empty.
 */
export interface Empty {
  type: 'Empty'
}

/**
 * The checklist has incomplete items.
 */
export interface Incomplete {
  type: 'Incomplete'
  /**
   * Whether all required items have been completed.
   */
  allRequiredItemsCompleted: boolean
  /**
   * List of items that are not completed.
   */
  incompleteItems: RequestedSupportingItem[]
}

/**
 * The checklist is populated and completed.
 */
export interface Completed {
  type: 'Completed'
}
